import Phaser from 'phaser';
import { prefersReducedMotion } from '../fx/motion';
import {
  GLEN_TIPS,
  LOADER_FAR_DRIFT_X,
  LOADER_FAR_DRIFT_Y,
  entranceSettled,
  glenProgress,
  loaderBackdrop,
  loaderStack,
  smoothGlenProgress,
} from './glenLoadPlan';
import { stage } from './stage';
import { LOADER_TEX, ensureLoaderPaint } from './glenPaint';
import { TEX } from './assets';
import { mountGlenLoader, type GlenLoaderOverlay } from './glenLoaderOverlay';

export { GLEN_TIPS, glenProgress };

interface Mote {
  sprite: Phaser.GameObjects.Image;
  x: number;
  y: number;
  vx: number;
  vy: number;
  sway: number;
  phase: number;
  spin: number;
  kind: 'fly' | 'leaf' | 'seed';
}

const FADE_MS = 850;
const FADE_REDUCED_MS = 280;

/**
 * Painted gate between the title and the hub.
 * The sky stays on the canvas. The sign, portrait, honey bar, and percent sit in a DOM
 * overlay so the spin and the sway are CSS transforms the compositor can keep running
 * while texture decode or scene create blocks the main thread.
 */
export class GlenLoadScene extends Phaser.Scene {
  private target = 0;
  private shown = 0;
  private age = 0;
  private exitT = 0;
  private leaving = false;
  private fading = false;
  private reduced = false;
  private viewW = 720;
  private viewH = 1280;
  private tipIndex = 0;
  private tipAt = 0;
  private tipFading = 0;
  private tipSwapped = false;
  private pendingTip = '';
  private reveal: (() => void | Promise<void>) | null = null;
  private revealTask: Promise<void> | null = null;
  private outroDone: (() => void) | null = null;
  private outroPromise: Promise<void> | null = null;
  private outroTimer = 0;
  private overlayHooked = false;

  private world: Phaser.GameObjects.Container | null = null;
  private overlay: GlenLoaderOverlay | null = null;
  private farBase = { x: 0, y: 0 };
  private midBase = { x: 0, y: 0 };
  private nearBase = { x: 0, y: 0 };
  private lightBase = { x: 0, y: 0 };
  private farImg: Phaser.GameObjects.Image | null = null;
  private midImg: Phaser.GameObjects.Image | null = null;
  private nearImg: Phaser.GameObjects.Image | null = null;
  private lightImg: Phaser.GameObjects.Image | null = null;
  private motes: Mote[] = [];

  constructor() {
    super('glen-load');
  }

  create(): void {
    const width = this.scale.width;
    const height = this.scale.height;
    this.viewW = width;
    this.viewH = height;
    this.reduced = prefersReducedMotion();
    this.age = 0;
    this.exitT = 0;
    this.leaving = false;
    this.fading = false;
    this.shown = 0;
    const camera = this.cameras.main;
    camera.setBackgroundColor('rgba(0,0,0,0)');
    camera.backgroundColor.alpha = 0;

    const stack = loaderStack();
    ensureLoaderPaint(this, Math.min(560, Math.round(width - 64)), stack.plankH);

    const cx = width / 2;
    const world = this.add.container(0, 0).setDepth(1);
    this.world = world;
    // Cover the scrolled camera, not just the layout, so the sky has no edge band.
    const sky = loaderBackdrop(width, height, stage.w, stage.h);
    this.farImg = this.add.image(sky.x, sky.y, LOADER_TEX.far).setDisplaySize(sky.w, sky.h);
    this.midImg = this.add.image(cx, height * 0.58, LOADER_TEX.mid).setDisplaySize(width * 1.24, height * 0.7);
    this.nearImg = this.add.image(cx, height * 0.8, LOADER_TEX.near).setDisplaySize(width * 1.3, height * 0.5);
    this.lightImg = this.add
      .image(width * 0.72, height * 0.22, LOADER_TEX.light)
      .setDisplaySize(width * 0.7, width * 0.7)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.55);
    this.farBase = { x: this.farImg.x, y: this.farImg.y };
    this.midBase = { x: this.midImg.x, y: this.midImg.y };
    this.nearBase = { x: this.nearImg.x, y: this.nearImg.y };
    this.lightBase = { x: this.lightImg.x, y: this.lightImg.y };
    world.add(this.farImg);
    world.add(this.midImg);
    world.add(this.nearImg);
    world.add(this.lightImg);
    this.seedMotes(world, width, height);
    this.ensureOverlay();
  }

  /** Honey shown so far, 0 to 1. The CSS spin does not read this. */
  shownAmount(): number {
    return this.shown;
  }

  /**
   * Hang the sign over the canvas. Safe to call more than once.
   * Create mounts it before the first paint. Later stalls cannot reach back and stop the keyframes.
   */
  mountOverlay(): void {
    const status = this.sys?.settings?.status ?? 0;
    if (status < Phaser.Scenes.CREATING) return;
    this.ensureOverlay();
  }

  private ensureOverlay(): void {
    if (!this.overlayHooked) {
      this.overlayHooked = true;
      this.events.once('shutdown', () => {
        this.overlayHooked = false;
        this.overlay?.destroy();
        this.overlay = null;
      });
    }
    if (this.overlay) return;
    this.reduced = prefersReducedMotion();
    this.overlay = mountGlenLoader(this, this.reduced);
    this.overlay.setShown(this.shown);
    if (this.pendingTip) this.overlay.setTip(this.pendingTip);
  }

  /** `amount` is 0 to 1. A tip replaces the rotating line until the next turn. */
  setProgress(amount: number, tip?: string): void {
    this.target = Math.max(this.target, glenProgress(amount));
    if (tip) {
      this.pendingTip = tip;
      this.tipFading = 0;
      this.tipSwapped = false;
      this.tipAt = this.age;
      this.overlay?.setTip(tip);
    }
  }

  releaseInput(): void {
    this.overlay?.releasePointer();
    if (this.input) this.input.enabled = false;
  }

  /**
   * Wait until the honey fill has caught the real load and `onReveal` has put the
   * next scene on screen, then fade. The portrait keeps spinning through the fade.
   * `onReveal` is where the hub is created and its first frame is painted.
   */
  playOutro(onReveal: () => void | Promise<void>): Promise<void> {
    if (this.outroPromise) return this.outroPromise;
    this.reveal = onReveal;
    this.leaving = true;
    this.outroPromise = new Promise((resolve) => {
      this.outroDone = resolve;
    });
    this.outroTimer = window.setTimeout(() => {
      void this.revealThenFade();
    }, 8000);
    return this.outroPromise;
  }

  override update(_time: number, delta: number): void {
    if (!this.overlay) this.ensureOverlay();
    const dt = Math.min(0.05, Math.max(0, delta / 1000));
    this.age += dt;
    this.shown = smoothGlenProgress(this.shown, this.target, dt);
    this.overlay?.setShown(this.shown);
    this.driftGlen();
    this.driftMotes(dt);
    this.turnTips(dt);
    this.stepExit(dt);
  }

  private driftGlen(): void {
    const amp = this.reduced ? 0.15 : 1;
    const t = this.age;
    if (this.farImg) {
      this.farImg.x = this.farBase.x + Math.sin(t * 0.07) * LOADER_FAR_DRIFT_X * amp;
      this.farImg.y = this.farBase.y + Math.sin(t * 0.05) * LOADER_FAR_DRIFT_Y * amp;
    }
    if (this.midImg) {
      this.midImg.x = this.midBase.x + Math.sin(t * 0.11 + 0.6) * 26 * amp;
      this.midImg.y = this.midBase.y + Math.sin(t * 0.08) * 8 * amp;
    }
    if (this.nearImg) {
      this.nearImg.x = this.nearBase.x + Math.sin(t * 0.15 + 1.4) * 40 * amp;
      this.nearImg.y = this.nearBase.y + Math.cos(t * 0.09) * 10 * amp;
    }
    if (this.lightImg) {
      this.lightImg.x = this.lightBase.x + Math.sin(t * 0.06) * 16 * amp;
      this.lightImg.y = this.lightBase.y + Math.cos(t * 0.04) * 8 * amp;
      this.lightImg.setAlpha(0.42 + Math.sin(t * 0.7) * 0.12);
    }
  }

  private seedMotes(world: Phaser.GameObjects.Container, width: number, height: number): void {
    const flies = this.reduced ? 5 : 8;
    const leaves = this.reduced ? 3 : 5;
    const seeds = this.reduced ? 3 : 5;
    const leafKey = this.textures.exists(TEX.vfxLeaf) ? TEX.vfxLeaf : LOADER_TEX.leaf;
    for (let i = 0; i < flies; i += 1) this.addMote(world, 'fly', LOADER_TEX.fly, width, height, i);
    for (let i = 0; i < leaves; i += 1) this.addMote(world, 'leaf', leafKey, width, height, i + 3);
    for (let i = 0; i < seeds; i += 1) this.addMote(world, 'seed', LOADER_TEX.seed, width, height, i + 9);
  }

  private addMote(
    world: Phaser.GameObjects.Container,
    kind: Mote['kind'],
    key: string,
    width: number,
    height: number,
    salt: number,
  ): void {
    const x = ((salt * 97) % 1000) / 1000 * width;
    const y = ((salt * 53) % 1000) / 1000 * height;
    const sprite = this.add.image(x, y, key);
    if (kind === 'fly') {
      sprite.setBlendMode(Phaser.BlendModes.ADD).setDisplaySize(22 + (salt % 4) * 4, 22 + (salt % 4) * 4).setAlpha(0.8);
    } else if (kind === 'leaf') {
      sprite.setDisplaySize(22, 14).setAngle(salt * 20);
    } else {
      sprite.setDisplaySize(16, 28).setAlpha(0.9);
    }
    world.add(sprite);
    const speed = this.reduced ? 0.35 : 1;
    this.motes.push({
      sprite,
      x,
      y,
      vx: kind === 'seed' ? 8 + (salt % 5) * 4 : kind === 'leaf' ? 6 + (salt % 4) * 3 : -4 + (salt % 6),
      vy: (kind === 'fly' ? 6 + (salt % 4) : kind === 'seed' ? 14 + (salt % 5) * 2 : 22 + (salt % 6) * 3) * speed,
      sway: 0.8 + (salt % 5) * 0.15,
      phase: salt,
      spin: kind === 'leaf' ? 28 + salt : 12,
      kind,
    });
  }

  private driftMotes(dt: number): void {
    const width = this.viewW;
    const height = this.viewH;
    for (let i = 0; i < this.motes.length; i += 1) {
      const mote = this.motes[i];
      mote.phase += dt;
      const sway = Math.sin(mote.phase * mote.sway) * (mote.kind === 'seed' ? 16 : 10);
      mote.x += (mote.vx + sway) * dt;
      mote.y += mote.vy * dt;
      if (mote.kind === 'fly') {
        mote.y += Math.sin(mote.phase * 1.6) * 8 * dt;
        mote.sprite.setAlpha(0.35 + 0.55 * (0.5 + 0.5 * Math.sin(mote.phase * 3.1)));
      } else {
        mote.sprite.angle += mote.spin * dt;
      }
      if (mote.y > height + 24) {
        mote.y = -24;
        mote.x = (mote.phase * 131) % width;
      }
      if (mote.x < -30) mote.x = width + 16;
      if (mote.x > width + 30) mote.x = -16;
      mote.sprite.setPosition(mote.x, mote.y);
    }
  }

  private turnTips(dt: number): void {
    if (!this.overlay || this.leaving) return;
    if (this.tipFading > 0) {
      this.tipFading = Math.max(0, this.tipFading - dt);
      if (this.tipFading <= 0.18 && !this.tipSwapped) {
        this.tipSwapped = true;
        this.tipIndex = (this.tipIndex + 1) % GLEN_TIPS.length;
        this.overlay.setTip(GLEN_TIPS[this.tipIndex] ?? '');
      }
      if (this.tipFading <= 0) this.tipSwapped = false;
      return;
    }
    if (this.age - this.tipAt < 3.2) return;
    this.tipAt = this.age;
    this.tipFading = 0.36;
  }

  private stepExit(dt: number): void {
    if (!this.leaving) return;
    if (!this.revealTask) {
      const ready = entranceSettled(this.age, this.reduced) && this.shown >= 0.995;
      if (!ready && this.age < 4) return;
      void this.revealThenFade();
    }
    if (!this.fading) return;
    this.exitT += dt;
    const fadeDur = (this.reduced ? FADE_REDUCED_MS : FADE_MS) / 1000;
    const fade = 1 - Math.min(1, this.exitT / fadeDur);
    this.world?.setAlpha(Math.max(0, fade));
    if (this.exitT >= fadeDur + 0.04) this.finishOutro();
  }

  private revealThenFade(): Promise<void> {
    if (this.revealTask) return this.revealTask;
    this.revealTask = (async () => {
      this.shown = 1;
      this.target = 1;
      this.overlay?.setShown(1);
      try {
        await this.reveal?.();
      } catch {
        // The hub still has to appear if a listener throws.
      }
      this.beginFade();
    })();
    return this.revealTask;
  }

  /** Fade only after the entered scene has been created and has painted. */
  private beginFade(): void {
    if (this.fading || !this.outroDone) return;
    this.fading = true;
    const ms = this.reduced ? FADE_REDUCED_MS : FADE_MS;
    this.releaseInput();
    this.overlay?.fade(ms);
    if (this.outroTimer) window.clearTimeout(this.outroTimer);
    this.outroTimer = window.setTimeout(() => this.finishOutro(), ms + 80);
  }

  private finishOutro(): void {
    if (!this.outroDone) return;
    if (this.outroTimer) {
      window.clearTimeout(this.outroTimer);
      this.outroTimer = 0;
    }
    const done = this.outroDone;
    this.outroDone = null;
    done();
  }
}
